/**
 * Page side of peer diagnostics (see packages/tv-engine/src/peerDiag.ts).
 *
 * With `?peerDiag=1` the page taps its own console from the first line,
 * switches the workers' taps on over a BroadcastChannel, runs a few cheap
 * environment probes (is the tab visible, does the animation loop run, is
 * the main thread being throttled, what does the browser think of the
 * network) and keeps everything in a bounded queue until the engine's
 * PeerDiagnosticsManager can ship it to the host.
 */
import { installPeerDiagConsoleTap, PEER_DIAG_CHANNEL, } from '@streamr/tv-engine';
const MAX_QUEUED_LINES = 8000;
const ENV_PERIOD_MS = 5000;
const LATENESS_TICK_MS = 250;
let requested;
let initialized = false;
const queue = [];
let droppedLines = 0;
const pageLoadedAtMs = Date.now();
function urlFlag(name) {
    try {
        const v = new URLSearchParams(window.location.search).get(name);
        return v === '1' || v === 'true';
    }
    catch {
        return false;
    }
}
/** this participant reports its diagnostics to the session */
export function isPeerDiagRequested() {
    requested ??= urlFlag('peerDiag');
    return requested;
}
/** this participant prints the diagnostics other participants report */
export function isPeerDiagSinkEnabled() {
    return urlFlag('peerDiagSink') || urlFlag('gapDiagnostics');
}
function enqueue(line) {
    queue.push(line);
    if (queue.length > MAX_QUEUED_LINES) {
        queue.shift();
        droppedLines += 1;
    }
}
/** oldest lines first, up to a serialized size budget */
export function drainPeerDiagLines(maxChars) {
    const out = [];
    let chars = 0;
    while (queue.length > 0) {
        const next = queue[0];
        const cost = next.msg.length + next.src.length + 40;
        if (out.length > 0 && chars + cost > maxChars)
            break;
        out.push(next);
        queue.shift();
        chars += cost;
    }
    return out;
}
export function getPeerDiagQueueState() {
    return { queued: queue.length, dropped: droppedLines };
}
function connectionSnapshot() {
    const c = navigator
        .connection;
    if (!c)
        return undefined;
    return {
        effectiveType: c.effectiveType,
        downlinkMbps: c.downlink,
        rttMs: c.rtt,
        saveData: c.saveData,
        type: c.type,
    };
}
function logDevice() {
    const nav = navigator;
    console.log(`[diag-device] ${JSON.stringify({
        ua: navigator.userAgent,
        platform: nav.userAgentData?.platform ?? navigator.platform,
        mobile: nav.userAgentData?.mobile,
        cores: navigator.hardwareConcurrency,
        deviceMemoryGb: nav.deviceMemory,
        screen: `${screen.width}x${screen.height}@${window.devicePixelRatio}`,
        window: `${window.innerWidth}x${window.innerHeight}`,
        crossOriginIsolated: window.crossOriginIsolated,
        online: navigator.onLine,
        connection: connectionSnapshot(),
        language: navigator.language,
        pageLoadedAtMs,
        url: `${window.location.origin}${window.location.pathname}`,
    })}`);
}
/**
 * Every 5 s: is the page visible and focused, how many animation frames ran,
 * and how late the main thread's timers fired. A hidden or covered tab shows
 * up as visibility 'hidden', an animation-frame rate near zero and timers
 * clamped to about one per second.
 */
function startEnvProbe() {
    let rafCount = 0;
    const onFrame = () => {
        rafCount += 1;
        requestAnimationFrame(onFrame);
    };
    requestAnimationFrame(onFrame);
    let lastTick = performance.now();
    let maxLatenessMs = 0;
    let ticks = 0;
    setInterval(() => {
        const now = performance.now();
        const lateness = now - lastTick - LATENESS_TICK_MS;
        if (lateness > maxLatenessMs)
            maxLatenessMs = lateness;
        lastTick = now;
        ticks += 1;
    }, LATENESS_TICK_MS);
    let lastReport = performance.now();
    setInterval(() => {
        const now = performance.now();
        const periodS = (now - lastReport) / 1000;
        lastReport = now;
        const memory = performance.memory;
        console.log(`[diag-env] ${JSON.stringify({
            visibility: document.visibilityState,
            focused: document.hasFocus(),
            rafPerSec: Math.round((rafCount / periodS) * 10) / 10,
            timerTicksPerSec: Math.round((ticks / periodS) * 10) / 10,
            timerMaxLatenessMs: Math.round(maxLatenessMs),
            online: navigator.onLine,
            connection: connectionSnapshot(),
            heapMb: memory
                ? Math.round(memory.usedJSHeapSize / 1048576)
                : undefined,
            queue: getPeerDiagQueueState(),
        })}`);
        rafCount = 0;
        ticks = 0;
        maxLatenessMs = 0;
    }, ENV_PERIOD_MS);
    const logEvent = (ev, extra = {}) => console.log(`[diag-event] ${JSON.stringify({ ev, ...extra })}`);
    document.addEventListener('visibilitychange', () => logEvent('visibilitychange', { visibility: document.visibilityState }));
    window.addEventListener('focus', () => logEvent('focus'));
    window.addEventListener('blur', () => logEvent('blur'));
    window.addEventListener('online', () => logEvent('online'));
    window.addEventListener('offline', () => logEvent('offline'));
    document.addEventListener('freeze', () => logEvent('freeze'));
    document.addEventListener('resume', () => logEvent('resume'));
    navigator.connection?.addEventListener?.('change', () => logEvent('connection-change', { connection: connectionSnapshot() }));
}
export function initPeerDiagnosticsPage() {
    if (initialized || !isPeerDiagRequested())
        return;
    initialized = true;
    installPeerDiagConsoleTap('main', enqueue);
    if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel(PEER_DIAG_CHANNEL);
        channel.onmessage = (event) => {
            if (event.data?.type !== 'lines')
                return;
            for (const line of event.data.lines)
                enqueue(line);
        };
        // workers are created at different times: keep announcing
        const enable = { type: 'enable' };
        let announcements = 0;
        const announce = () => {
            channel.postMessage(enable);
            announcements += 1;
            setTimeout(announce, announcements < 120 ? 500 : 10_000);
        };
        announce();
    }
    console.log('[diag-tap] main console tap installed');
    logDevice();
    startEnvProbe();
}
